import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Switch,
  DatePicker,
  Upload,
  Button,
  Space,
  message,
  Typography,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { eventService, CreateEventData, Event } from '../services/events';
import type { UploadFile } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { staffService, Staff } from '../services/staffs';

const { TextArea } = Input;
const { Title } = Typography;

export const EventFormPage: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'calendar.update');
  const canDelete = hasPermission(user, 'calendar.delete');
  const [submitting, setSubmitting] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<UploadFile[]>([]);
  const [staffs, setStaffs] = useState<Staff[]>([]);
  const [loadingStaffs, setLoadingStaffs] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isEdit && id) {
      loadEvent(parseInt(id, 10));
    } else {
      form.setFieldsValue({
        type: 'appointment',
        all_day: false,
        alert: false,
      });
    }
  }, [isEdit, id, form]);

  useEffect(() => {
    loadStaffs();
  }, []);

  const loadStaffs = async () => {
    setLoadingStaffs(true);
    try {
      const response = await staffService.getAll();
      setStaffs(Array.isArray(response) ? response : []);
    } catch {
      message.error('Failed to load staffs');
    } finally {
      setLoadingStaffs(false);
    }
  };

  const loadEvent = async (eventId: number) => {
    setLoadingEvent(true);
    try {
      const event = await eventService.getById(eventId);
      form.setFieldsValue({
        ...event,
        start: dayjs(event.start),
        end: dayjs(event.end),
      });

      // Load attachment if exists
      if (event.attachment) {
        setAttachmentFile([{
          uid: '-1',
          name: 'attachment',
          status: 'done',
          url: `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000'}/storage/${event.attachment}`,
        }]);
      }
    } catch {
      message.error('Failed to load event');
      navigate('/calendar');
    } finally {
      setLoadingEvent(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      
      // Handle attachment
      if (attachmentFile.length > 0 && attachmentFile[0].originFileObj) {
        formData.append('attachment', attachmentFile[0].originFileObj);
      } else if (isEdit && attachmentFile.length === 0) {
        // If editing and no file, send empty string to remove attachment
        formData.append('attachment', '');
      }

      // Handle other fields
      Object.keys(values).forEach(key => {
        if (key === 'attachment') {
          // Skip, handled above
          return;
        } else if (key === 'start' || key === 'end') {
          if (values[key]) {
            formData.append(key, values[key].format('YYYY-MM-DD HH:mm:ss'));
          }
        } else if (values[key] !== undefined && values[key] !== null) {
          if (typeof values[key] === 'boolean') {
            formData.append(key, values[key] ? '1' : '0');
          } else {
            formData.append(key, values[key].toString());
          }
        }
      });

      if (isEdit && id) {
        await eventService.update(parseInt(id, 10), formData);
        message.success('Event updated successfully');
      } else {
        await eventService.create(formData);
        message.success('Event created successfully');
      }
      
      navigate('/calendar');
    } catch {
      message.error(isEdit ? 'Failed to update event' : 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || !id) return;
    
    try {
      await eventService.delete(parseInt(id, 10));
      message.success('Event deleted successfully');
      navigate('/calendar');
    } catch {
      message.error('Failed to delete event');
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/calendar')}
          style={{ marginBottom: 16 }}
        >
          Back
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {isEdit ? 'Edit Event' : 'Add Events'}
        </Title>
        <div style={{ color: '#999', marginTop: 8 }}>Calendar / {isEdit ? 'Edit Event' : 'Add Events'}</div>
      </div>


      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={loadingEvent || (isEdit && !canUpdate)}
      >
        <Card title="Event Details" style={{ marginBottom: 24 }}>
          <Form.Item
            name="customer_name"
            label="Customer"
            rules={[{ required: true, message: 'Please enter customer name' }]}
          >
            <Input placeholder="Enter Name" />
          </Form.Item>

          <Form.Item name="location" label="Location">
            <Input placeholder="Enter Location" />
          </Form.Item>

          <Form.Item
            name="staff_id"
            label="Staff"
            dependencies={['type']}
            rules={[
              ({ getFieldValue }) => ({
                required: ['appointment', 'leave'].includes(getFieldValue('type')),
                message: 'Please select staff',
              }),
            ]}
          >
            <Select
              placeholder="Select Staff"
              loading={loadingStaffs}
              allowClear
              showSearch
              optionFilterProp="label"
              options={staffs.map((staff) => ({
                value: staff.id,
                label: staff.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="Event Type"
            rules={[{ required: true, message: 'Please select type' }]}
          >
            <Select placeholder="Select Event Type">
              <Select.Option value="appointment">Appointment</Select.Option>
              <Select.Option value="leave">Leave</Select.Option>
              <Select.Option value="deadline">Deadline</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="all_day" label="All-day" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            name="start"
            label="Start"
            rules={[{ required: true, message: 'Please select start time' }]}
          >
            <DatePicker
              showTime={!form.getFieldValue('all_day')}
              format={form.getFieldValue('all_day') ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm:ss'}
              style={{ width: '100%' }}
              placeholder="Enter Start Time"
            />
          </Form.Item>

          <Form.Item
            name="end"
            label="End"
            rules={[{ required: true, message: 'Please select end time' }]}
          >
            <DatePicker
              showTime={!form.getFieldValue('all_day')}
              format={form.getFieldValue('all_day') ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm:ss'}
              style={{ width: '100%' }}
              placeholder="Enter End Time"
            />
          </Form.Item>

          <Form.Item name="alert" label="Alert" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item label="Attachment">
            <Upload
              fileList={attachmentFile}
              beforeUpload={() => false}
              onChange={(info) => {
                setAttachmentFile(info.fileList);
              }}
              maxCount={1}
              accept=".pdf,.png,.jpg,.jpeg"
              onRemove={() => setAttachmentFile([])}
            >
              <Button icon={<UploadOutlined />}>Upload PDF / PNG file</Button>
            </Upload>
          </Form.Item>

          <Form.Item name="note" label="Notes">
            <TextArea rows={4} placeholder="Enter notes here" />
          </Form.Item>
        </Card>

        <div style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24
        }}>
          <Space>
            {isEdit && canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
            <Button onClick={() => navigate('/calendar')}>Cancel</Button>
            {(!isEdit || canUpdate) && (
              <Button type="primary" htmlType="submit" loading={submitting}>
                Save
              </Button>
            )}
          </Space>
        </div>
      </Form>
    </Card>
  );
};
